import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { PageHeader, Modal, Toast } from "@/components/Layout";
import api, { errorText, soft } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { downloadFile } from "@/lib/download";
import { lock } from "@/lib/perm";
import ListEditor from "@/components/ListEditor";
import Select from "@/components/Select";

const EMPTY = {
  nivel_post: "", denumire: "", grad_treapta: "", scop: "", timp_lucru: "", norma_ore: "",
  studii_nivel: "", studii_specialitate: "", perfectionari: "", calculator: "", limbi: "", abilitati: "", cerinte_specifice: "", competenta_manageriala: "",
  atributii: [],
  rel_subordonat_fata_de: "", rel_superior_pentru: "", rel_functionale: "", rel_control: "", rel_reprezentare: "",
  rel_externe_autoritati: "", rel_externe_organizatii: "", rel_externe_persoane_juridice: "", delegare: "",
};
const dutyKey = (s) => String(s || "").replace(/\s+/g, " ").trim().replace(/[.;,]+$/, "").toLowerCase();

function Field({ label, children, wide }) {
  return <div style={wide ? { gridColumn: "1/-1" } : undefined}><label className="label">{label}</label>{children}</div>;
}

// Fișa postului: același editor pentru un post (/fisa-post/post/:id) și pentru un raport de muncă (/fisa-post/raport/:id).
export default function JobDescription() {
  const { id } = useParams();
  const { pathname, search } = useLocation();
  const nav = useNavigate();
  const { has } = useAuth();
  const isPost = pathname.includes("/fisa-post/post/");
  const base = `/stat-functii/job-descriptions/${isPost ? "post" : "employment"}/${id}`;
  const canEdit = has("job_descriptions.edit");
  const [c, setC] = useState(EMPTY);
  const [info, setInfo] = useState({ exists: false, postHas: false });
  const [title, setTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ text: "", type: "info" });
  const [levels, setLevels] = useState([]);
  const [library, setLibrary] = useState(false);
  const [signers, setSigners] = useState(null);
  const toast = (text, type = "info", ms = 3000) => { setMsg({ text, type }); if (ms) setTimeout(() => setMsg({ text: "" }), ms); };
  const set = (patch) => { setC(x => ({ ...x, ...patch })); setDirty(true); };

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(base);
        let content = { ...EMPTY, ...r.data.content };
        setInfo({ exists: r.data.exists, postHas: !!r.data.post_has_description });
        if (!isPost && new URLSearchParams(search).get("preia") === "1") {      // „Preia fișa de post din Stat de funcții”
          const p = await api.get(`${base}/from-post`);
          content = { ...EMPTY, ...p.data.content }; setDirty(true);
          toast(p.data.post_has_description ? "Fișa postului a fost preluată și completată cu datele raportului. Salvează ca să o păstrezi."
            : "Postul nu are încă fișă; s-au preluat doar datele raportului. Salvează ca să o păstrezi.", "info", 6000);
        }
        setC(content);
        // titlul paginii
        if (isPost) setTitle(`Post ${r.data.post?.code || ""}`);
        else { const e = await soft(api.get("/mdm/employments"), { data: [] }); const x = (e.data || []).find(z => z.id === id); setTitle(x ? `Raport de muncă ${x.marca} — ${x.user_name || ""}` : "Raport de muncă"); }
      } catch (e) { toast(errorText(e, "Fișa nu a putut fi încărcată"), "error", 0); }
      finally { setLoading(false); }
      soft(api.get(`${base}/signers`), { data: null }).then(r => setSigners(r.data));
      const cfg = await soft(api.get("/config"), { data: null });
      setLevels(cfg.data?.education_levels || []);
    })();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setBusy(true);
    try { await api.put(base, c); setDirty(false); setInfo(i => ({ ...i, exists: true })); toast("Fișa a fost salvată", "success", 2000); return true; }
    catch (e) { const d = e?.response?.data?.detail; toast(Array.isArray(d) ? "Verifică câmpurile completate (lungimi sau număr de rânduri)." : errorText(e, "Eroare la salvare"), "error", 6000); return false; }
    finally { setBusy(false); }
  };
  const pdf = async () => {
    if (dirty && canEdit && !(await save())) return;
    setBusy(true);
    try { await downloadFile(`${base}/pdf`, "fisa_post.pdf"); } catch (e) { toast(e.message, "error", 6000); } finally { setBusy(false); }
  };
  const reload = async () => {
    if (!window.confirm("Reîncarci datele din post? Textele completate acum vor fi înlocuite (nu se salvează până nu apeși Salvează).")) return;
    try {
      const r = await api.get(isPost ? `${base}/defaults` : `${base}/from-post`);
      setC({ ...EMPTY, ...r.data.content }); setDirty(true);
    } catch (e) { toast(errorText(e), "error"); }
  };
  const addFromLibrary = (text) => {
    if (c.atributii.some(x => dutyKey(x) === dutyKey(text))) { toast("Atribuția există deja în fișă", "info", 2000); return; }
    set({ atributii: [...c.atributii, text] });
  };
  const conducere = useMemo(() => /conducere/i.test(c.nivel_post), [c.nivel_post]);
  const back = () => { if (dirty && !window.confirm("Ai modificări nesalvate. Ieși fără să le salvezi?")) return; nav(-1); };

  if (loading) return <div style={{ padding: 40 }}>Se încarcă...</div>;
  const ro = !canEdit;
  return (
    <>
      <PageHeader title="Fișa postului" subtitle={`${title}${info.exists ? "" : " — nesalvată încă"}`}
        actions={<>
          <button className="btn btn-secondary" onClick={back}>← Înapoi</button>
          {canEdit && <button className="btn btn-secondary" onClick={reload} data-testid="jd-reload">{isPost ? "Reîncarcă din post" : "Preia din Stat de funcții"}</button>}
          {canEdit && <button className="btn btn-primary" onClick={save} disabled={busy || !dirty} data-testid="jd-save" {...lock(has("job_descriptions.edit"))}>Salvează</button>}
          <button className="btn btn-blue" onClick={pdf} disabled={busy} data-testid="jd-pdf">Generează PDF</button>
        </>} />
      <Toast msg={msg.text} type={msg.type} />
      {ro && <div className="view-only-note">Vizualizare — nu ai dreptul să redactezi fișe de post; poți genera PDF-ul.</div>}
      <>
        <fieldset disabled={ro} className={ro ? "view-only" : undefined} style={ro ? undefined : { border: 0, padding: 0, margin: 0, minWidth: 0 }}>
          <div className="card" style={{ padding: 18, marginBottom: 14 }}>
            <h3 style={{ marginTop: 0 }}>A. Informații generale privind postul</h3>
            <div className="grid-2">
              <Field label="Nivelul postului (funcție de execuție sau de conducere)">
                <Select className="select" value={c.nivel_post} onChange={e => set({ nivel_post: e.target.value })} data-testid="jd-nivel">
                  <option value="">— nespecificat —</option><option>Funcție de execuție</option><option>Funcție de conducere</option>
                </Select></Field>
              <Field label="Denumirea postului"><input className="input" value={c.denumire} onChange={e => set({ denumire: e.target.value })} data-testid="jd-denumire" /></Field>
              <Field label="Gradul / Treapta profesional(ă)"><input className="input" value={c.grad_treapta} onChange={e => set({ grad_treapta: e.target.value })} /></Field>
              <Field label="Timpul de lucru"><input className="input" value={c.timp_lucru} onChange={e => set({ timp_lucru: e.target.value })} placeholder="ex: Normă întreagă" /></Field>
              <Field label="Norma de lucru (ore/zi)"><input className="input" value={c.norma_ore} onChange={e => set({ norma_ore: e.target.value })} /></Field>
              <Field label="Scopul principal al postului" wide><textarea className="textarea" rows={3} value={c.scop} onChange={e => set({ scop: e.target.value })} data-testid="jd-scop" /></Field>
            </div>
            {canEdit && <div style={{ textAlign: "right", marginTop: 12 }}><button type="button" className="btn btn-primary" onClick={save} disabled={busy || !dirty} data-testid="jd-save-a">Salvează</button></div>}
          </div>

          <div className="card" style={{ padding: 18, marginBottom: 14 }}>
            <h3 style={{ marginTop: 0 }}>B. Condiții specifice pentru ocuparea postului</h3>
            <div className="grid-2">
              <Field label="Studii — nivel">
                <Select className="select" value={c.studii_nivel} onChange={e => set({ studii_nivel: e.target.value })}>
                  <option value="">— nespecificat —</option>
                  {c.studii_nivel && !levels.includes(c.studii_nivel) && <option>{c.studii_nivel}</option>}
                  {levels.map(l => <option key={l}>{l}</option>)}
                </Select></Field>
              <Field label="Studii de specialitate"><input className="input" value={c.studii_specialitate} onChange={e => set({ studii_specialitate: e.target.value })} /></Field>
              <Field label="Perfecționări (specializări)" wide><textarea className="textarea" rows={2} value={c.perfectionari} onChange={e => set({ perfectionari: e.target.value })} /></Field>
              <Field label="Cunoștințe de operare/programare pe calculator (necesitate și nivel)" wide><textarea className="textarea" rows={2} value={c.calculator} onChange={e => set({ calculator: e.target.value })} /></Field>
              <Field label="Limbi străine (necesitate și nivel) cunoscute" wide>
                <textarea className="textarea" rows={2} value={c.limbi} onChange={e => set({ limbi: e.target.value })} data-testid="jd-limbi" placeholder="ex: Engleză — nivel mediu; Franceză — începător" /></Field>
              <Field label="Abilități și aptitudini necesare" wide><textarea className="textarea" rows={2} value={c.abilitati} onChange={e => set({ abilitati: e.target.value })} /></Field>
              <Field label="Cerințe specifice (aviz/autorizație prevăzută de lege, după caz)" wide><textarea className="textarea" rows={2} value={c.cerinte_specifice} onChange={e => set({ cerinte_specifice: e.target.value })} /></Field>
              {conducere && <Field label="Competența managerială (cunoștințe de management, calități și aptitudini manageriale)" wide>
                <textarea className="textarea" rows={3} value={c.competenta_manageriala} onChange={e => set({ competenta_manageriala: e.target.value })} /></Field>}
            </div>
            {canEdit && <div style={{ textAlign: "right", marginTop: 12 }}><button type="button" className="btn btn-primary" onClick={save} disabled={busy || !dirty} data-testid="jd-save-b">Salvează</button></div>}
          </div>

          <div className="card" style={{ padding: 18, marginBottom: 14 }} data-testid="jd-atributii">
            <h3 style={{ marginTop: 0 }}>C. Atribuțiile postului</h3>
            <ListEditor value={c.atributii} onChange={v => set({ atributii: v })} rows={2} placeholder="Atribuția…" addLabel="+ Adaugă atribuție" testid="jd-duties" readOnly={ro}
              extraActions={has("duty_library.view") && <button type="button" className="btn btn-secondary" onClick={() => setLibrary(true)} data-testid="jd-from-library">+ Adaugă din bibliotecă</button>} />
            {canEdit && <div style={{ textAlign: "right", marginTop: 12 }}><button type="button" className="btn btn-primary" onClick={save} disabled={busy || !dirty} data-testid="jd-save-c">Salvează</button></div>}
          </div>

          <div className="card" style={{ padding: 18, marginBottom: 14 }}>
            <h3 style={{ marginTop: 0 }}>D. Sfera relațională a titularului postului</h3>
            <div className="grid-2">
              <Field label="Relații ierarhice — subordonat față de"><textarea className="textarea" rows={2} value={c.rel_subordonat_fata_de} onChange={e => set({ rel_subordonat_fata_de: e.target.value })} /></Field>
              <Field label="Relații ierarhice — superior pentru"><textarea className="textarea" rows={2} value={c.rel_superior_pentru} onChange={e => set({ rel_superior_pentru: e.target.value })} /></Field>
              <Field label="Relații funcționale"><textarea className="textarea" rows={2} value={c.rel_functionale} onChange={e => set({ rel_functionale: e.target.value })} /></Field>
              <Field label="Relații de control"><textarea className="textarea" rows={2} value={c.rel_control} onChange={e => set({ rel_control: e.target.value })} /></Field>
              <Field label="Relații de reprezentare"><textarea className="textarea" rows={2} value={c.rel_reprezentare} onChange={e => set({ rel_reprezentare: e.target.value })} /></Field>
              <Field label="Externe — cu autorități și instituții publice"><textarea className="textarea" rows={2} value={c.rel_externe_autoritati} onChange={e => set({ rel_externe_autoritati: e.target.value })} /></Field>
              <Field label="Externe — cu organizații internaționale"><textarea className="textarea" rows={2} value={c.rel_externe_organizatii} onChange={e => set({ rel_externe_organizatii: e.target.value })} /></Field>
              <Field label="Externe — cu persoane juridice private"><textarea className="textarea" rows={2} value={c.rel_externe_persoane_juridice} onChange={e => set({ rel_externe_persoane_juridice: e.target.value })} /></Field>
              <Field label="Delegarea de atribuții și competență" wide><textarea className="textarea" rows={3} value={c.delegare} onChange={e => set({ delegare: e.target.value })} /></Field>
            </div>
            {canEdit && <div style={{ textAlign: "right", marginTop: 12 }}><button type="button" className="btn btn-primary" onClick={save} disabled={busy || !dirty} data-testid="jd-save-d">Salvează</button></div>}
          </div>
        </fieldset>
      </>
      {signers && (
        <div className="card" style={{ padding: 14, marginBottom: 14, fontSize: 13 }} data-testid="jd-signers">
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Semnături din PDF (se completează automat)</div>
          {[["Întocmește", signers.e], ["Luat la cunoștință", signers.f], ["Contrasemnează", signers.g]].map(([lab, p]) => (
            <div key={lab}><span style={{ color: "#64748B" }}>{lab}:</span> {p?.name ? <b>{p.name}{p.function ? ` — ${p.function}` : ""}</b> : <span style={{ color: "#94A3B8" }}>— (rămâne gol)</span>}</div>
          ))}
          <div style={{ fontSize: 11.5, color: "#64748B", marginTop: 6 }}>Ca la PlanCo: fișa se întocmește de managerul direct al raportului; un manager de structură nu își întocmește singur fișa — o întocmește managerul structurii superioare, iar managerul acestuia o contrasemnează.</div>
        </div>
      )}
      <div style={{ fontSize: 12, color: "#64748B", marginBottom: 20 }}>
        Fișa se generează în PDF, la cerere; nu se salvează în Dosarul electronic. Acolo se încarcă fișa semnată și scanată. Semnăturile și datele din PDF rămân necompletate.
      </div>
      {library && <LibraryPicker onClose={() => setLibrary(false)} onPick={addFromLibrary} />}
    </>
  );
}

function LibraryPicker({ onClose, onPick }) {
  const [q, setQ] = useState("");
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const run = async (text) => {
    setLoading(true);
    try { const r = await api.get("/stat-functii/duties", { params: { q: text } }); setGroups(r.data.groups || []); } finally { setLoading(false); }
  };
  useEffect(() => { run(""); }, []);
  return (
    <Modal title="Adaugă din Biblioteca de atribuții" onClose={onClose} width={760}>
      <form onSubmit={e => { e.preventDefault(); run(q); }} style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <input className="input" placeholder="Caută în atribuții…" value={q} onChange={e => setQ(e.target.value)} data-testid="jd-lib-q" />
        <button type="submit" className="btn btn-primary" data-testid="jd-lib-search">Caută</button>
      </form>
      {loading ? <div>Se încarcă...</div> : groups.length === 0 ? <div style={{ color: "#64748B" }}>Nicio atribuție găsită.</div> : groups.map(g => (
        <div key={g.unit_id} style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--navy-3)", marginBottom: 4 }}>{g.unit_name}</div>
          {g.duties.map((d, i) => (
            <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "6px 0", borderTop: "1px solid #F1F5F9" }}>
              <div style={{ flex: 1, fontSize: 13 }}>{d.text}<div style={{ fontSize: 11, color: "#94A3B8" }}>în {d.count} {d.count === 1 ? "fișă" : "fișe"}</div></div>
              <button type="button" className="btn btn-secondary" style={{ padding: "3px 10px", fontSize: 12 }} onClick={() => onPick(d.text)} data-testid="jd-lib-add">Adaugă</button>
            </div>
          ))}
        </div>
      ))}
    </Modal>
  );
}
